import React, { useEffect, useState } from "react";
import axios from "axios";
import queryString from "query-string";

import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import Button from "designV2/components/Button";
import { endPoint } from "utils/config";
import logo from "assets/img/sparkLoginLogo.svg";
import "./standalonePage.css";

/**
 * BankInfo - hands a shop or driver over to Stripe to set up payouts.
 *
 * Two visits: the first creates a Stripe Connect account and leaves for
 * Stripe's own onboarding, the second comes back with a `code` in the
 * query string and exchanges it. Nothing is collected on this page —
 * bank details only ever exist on Stripe's side.
 */
const BankInfo = (props) => {
  const params = queryString.parse(props.location.search);
  const code = params.code;
  const driverToken = params.token;

  // The driver app passes its token in the URL, and the verification
  // below signs with whatever is in storage — so it has to land first.
  // The old version set it in a second effect that ran afterwards, so
  // a returning driver verified with the previous session's token.
  if (props.forDriver && driverToken) {
    window.localStorage.setItem("token", driverToken.toString());
  }

  const [verifying, setVerifying] = useState(Boolean(code));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!code) return;

    axios
      .post(
        `${window.env.API_URL}/connect/verify-account`,
        { code },
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            authorization: window.localStorage.getItem("token"),
          },
        }
      )
      .then((response) => {
        if (response.data.redirect) {
          window.location = endPoint + "/payment/success";
          return;
        }
        const shopType = window.localStorage.getItem("shopType");
        window.location.href =
          endPoint + `/shopAdmin/${shopType === "Restaurant" ? "Menu" : "Categories"}`;
      })
      .catch(() => {
        if (props.forDriver) {
          window.location = endPoint + "/payment/fail";
          return;
        }
        // A shop admin used to be left staring at a "press confirm"
        // message with a button wired to do nothing.
        setVerifying(false);
        setError("We could not finish setting up your payouts. Please try again.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreateAccount = () => {
    if (creating) return;
    setCreating(true);
    setError("");

    axios
      .post(
        `${window.env.API_URL}/connect/create-account`,
        {},
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            authorization: window.localStorage.getItem("token"),
          },
        }
      )
      .then((response) => {
        window.location.href = response.data.accountLink;
      })
      .catch(() => {
        setCreating(false);
        setError("We could not start the setup. Please try again in a moment.");
      });
  };

  return (
    <div className="standalonePage">
      <div className="standaloneInner isNarrow">
        <div className="standaloneBrand">
          <img src={logo} alt="Spark" />
        </div>

        <div className="standaloneCard">
          <div className="standaloneResult">
            <span className="standaloneResultIcon info">
              <AccountBalanceIcon />
            </span>

            {verifying ? (
              <React.Fragment>
                <h1 className="standaloneResultTitle">Finishing up</h1>
                <p className="standaloneResultText">
                  We are confirming your details with Stripe. This only takes a moment.
                </p>
                <Button variant="secondary" loading>
                  Confirming
                </Button>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <h1 className="standaloneResultTitle">Where should we send your money?</h1>
                <p className="standaloneResultText">
                  Spark pays out through Stripe. Setting up an account takes about a minute, and
                  your bank details are held by Stripe rather than by us.
                </p>

                {error && (
                  <div className="dsFormMessage error" role="alert">
                    <ErrorOutlineIcon />
                    <span>{error}</span>
                  </div>
                )}

                <Button
                  variant="primary"
                  size="lg"
                  onClick={onCreateAccount}
                  loading={creating}
                >
                  {creating ? "Opening Stripe" : "Set up payouts"}
                </Button>
              </React.Fragment>
            )}
          </div>
        </div>

        <div className="standaloneFoot">
          You will be taken to Stripe to enter your bank details, then brought back here.
        </div>
      </div>
    </div>
  );
};

export default BankInfo;
